import { t } from 'i18n'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  createResortHeatmap,
  deleteResortHeatmap,
  listResortHeatmaps,
  renameResortHeatmap,
  setDefaultResortHeatmap
} from 'services'
import { HeatmapDefinition } from 'store/ducks/report/types'

export interface UseHeatmapsReturn {
  heatmaps: HeatmapDefinition[]
  loading: boolean
  selectedHeatmapIds: Record<string, string>
  heatmapsBySeason: Record<string, HeatmapDefinition[]>
  loadHeatmaps: () => Promise<void>
  handleSetDefault: (heatmapId: string) => Promise<void>
  handleDelete: (heatmap: HeatmapDefinition) => Promise<void>
  handleRename: (heatmapId: string, newName: string) => Promise<void>
  handleCreate: (params: {
    name: string
    season: string
    fileName: string
  }) => Promise<{ putUrl: string; id: string }>
  handleHeatmapSelection: (season: string, heatmapId: string) => void
  getSelectedHeatmap: (season: string) => HeatmapDefinition | undefined
  refreshHeatmaps: () => Promise<HeatmapDefinition[]>
}

export const useHeatmaps = (): UseHeatmapsReturn => {
  const [heatmaps, setHeatmaps] = useState<HeatmapDefinition[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedHeatmapIds, setSelectedHeatmapIds] = useState<
    Record<string, string>
  >({})

  // Load heatmaps on mount
  useEffect(() => {
    loadHeatmaps()
  }, [])

  // Auto-select default heatmaps when heatmaps are loaded
  useEffect(() => {
    const defaultSelections: Record<string, string> = {}
    heatmaps.forEach(heatmap => {
      if (heatmap.isDefault) {
        defaultSelections[heatmap.season] = heatmap.id
      }
    })
    setSelectedHeatmapIds(defaultSelections)
  }, [heatmaps])

  // Group heatmaps by season
  const heatmapsBySeason = heatmaps.reduce(
    (acc, heatmap) => {
      const season = heatmap.season
      if (!acc[season]) {
        acc[season] = []
      }
      acc[season].push(heatmap)
      return acc
    },
    {} as Record<string, HeatmapDefinition[]>
  )

  const loadHeatmaps = async () => {
    try {
      setLoading(true)
      const data = await listResortHeatmaps()
      // console.log('Loaded heatmaps:', data)
      setHeatmaps(data)
    } catch (error: any) {
      console.error('Failed to load heatmaps:', error)
      toast.error(error?.response?.data?.message || 'Failed to load heatmaps')
    } finally {
      setLoading(false)
    }
  }

  const refreshHeatmaps = async (): Promise<HeatmapDefinition[]> => {
    const data = await listResortHeatmaps()
    setHeatmaps(data)
    return data
  }

  const handleSetDefault = async (heatmapId: string) => {
    try {
      const updated = await setDefaultResortHeatmap(heatmapId)
      setHeatmaps(updated)
      toast.success(t('heatmaps.saved') || t('save') || 'Saved')
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to set default')
    }
  }

  const handleDelete = async (heatmap: HeatmapDefinition) => {
    // Use heatmap-specific translation key with fallbacks
    const confirmMessage =
      t('heatmaps.deleteWarning') ||
      t('heatmaps.areYouSure') ||
      'Are you sure you want to delete this heatmap?'
    if (!window.confirm(confirmMessage)) return

    try {
      const updated = await deleteResortHeatmap(heatmap.id)
      setHeatmaps(updated)
      toast.success(t('heatmaps.deleted') || 'Deleted')
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to delete heatmap')
    }
  }

  const handleRename = async (heatmapId: string, newName: string) => {
    if (!newName.trim()) return

    try {
      const updated = await renameResortHeatmap(heatmapId, newName.trim())
      setHeatmaps(updated)
      toast.success(t('heatmaps.saved') || t('save') || 'Saved')
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to rename heatmap')
    }
  }

  const handleCreate = async (params: {
    name: string
    season: string
    fileName: string
  }) => {
    return await createResortHeatmap(params)
  }

  const handleHeatmapSelection = (season: string, heatmapId: string) => {
    setSelectedHeatmapIds(prev => ({
      ...prev,
      [season]: heatmapId
    }))
  }

  const getSelectedHeatmap = (
    season: string
  ): HeatmapDefinition | undefined => {
    const selectedId = selectedHeatmapIds[season]
    return heatmaps.find(h => h.id === selectedId)
  }

  return {
    heatmaps,
    loading,
    selectedHeatmapIds,
    heatmapsBySeason,
    loadHeatmaps,
    handleSetDefault,
    handleDelete,
    handleRename,
    handleCreate,
    handleHeatmapSelection,
    getSelectedHeatmap,
    refreshHeatmaps
  }
}
